/**
 * Utilities for working with parent-stored virtual rows.
 *
 * Parent-stored virtual rows store their editable properties (timeScale, heightPx, label)
 * in the parent row's `nestedRows` attribute instead of being hydrated as separate
 * database rows. This keeps virtual rows truly virtual while maintaining editability.
 *
 * Generic over MutableRow — no DOM dependencies.
 */

import type { Option } from "@tento-lona/sheets";
import { Json, type JsonObject, type RowLocalId } from "@tento-lona/sheets";
import type { RowKey } from "@tento-lona/sheets";
import type { ViewModelRow, ViewModelTreeRef } from "../view-model/view-model.ts";

/**
 * Minimal row interface for ParentStoredRow utilities.
 * Satisfied by any `RowLocalId`-keyed view-model row — the SDK's
 * `SheetRow.Joined` and its render-side projections.
 */
export interface ParentStoredCompatible extends ViewModelRow<RowLocalId> {
  readonly treeRef: ViewModelTreeRef<RowLocalId>;
}

/**
 * A row stored inside its parent's attributes (`nestedRows`) rather than as
 * its own server row.
 */
export namespace ParentStoredRow {
  /**
   * Properties that can be stored in nestedRows.
   */
  export type NestedProperties = {
    timeScale?: string;
    heightPx?: number;
    label?: string;
  };

  /**
   * The nestedRows attribute structure on the parent row.
   */
  export type NestedRowsAttribute = {
    [preset: string]: NestedProperties;
  };

  /**
   * Get the parent info for a virtual row.
   * Returns null if the row is not a parent-stored virtual row.
   */
  export function parentInfoOf(
    row: ParentStoredCompatible,
  ): Option<{ parentHex: string; preset: string }> {
    if (!row.isVirtual) return null;
    if (!row.parentStored) return null;
    const virt = row.id.key.asVirtual();
    if (!virt) return null;
    return { parentHex: virt.parentHex, preset: virt.preset };
  }

  /**
   * Get the forward target for a row update.
   * Returns the parent row and preset if the row is a parent-stored virtual row,
   * otherwise returns null (update should proceed normally).
   */
  export function forwardTargetOf<R extends ParentStoredCompatible>(
    row: R,
  ): Option<{ parent: R; preset: string }> {
    const info = parentInfoOf(row);
    if (!info) return null;
    const tree = row.treeRef.tree;
    if (!tree) return null;
    // Find parent by iterating byId (parentHex → r_<hex> canonical form)
    const parentKey = `r_${info.parentHex}`;
    for (const [localId, r] of tree.byId) {
      if (localId.toString() === parentKey) {
        return { parent: r as R, preset: info.preset };
      }
    }
    return null;
  }

  /**
   * Update nested properties on the parent row's nestedRows attribute.
   * This creates the nestedRows structure if it doesn't exist.
   */
  export function updateNestedProperties(
    parent: ParentStoredCompatible,
    preset: string,
    updates: Partial<NestedProperties>,
  ): JsonObject {
    const currentAttributes = parent.attributes() ?? {};
    const currentNestedRows = Json.child(currentAttributes, "nestedRows") ?? {};
    const currentPresetProps = Json.child(currentNestedRows, preset) ?? {};

    const newPresetProps = {
      ...currentPresetProps,
      ...(updates.timeScale !== undefined ? { timeScale: updates.timeScale } : {}),
      ...(updates.heightPx !== undefined ? { heightPx: updates.heightPx } : {}),
      ...(updates.label !== undefined ? { label: updates.label } : {}),
    };

    const newNestedRows = {
      ...currentNestedRows,
      [preset]: newPresetProps,
    };

    return {
      ...currentAttributes,
      nestedRows: newNestedRows,
    };
  }

  /**
   * Get the nested properties for a given preset from the parent row.
   */
  export function nestedPropertiesOf(
    parent: ParentStoredCompatible,
    preset: string,
  ): Option<NestedProperties> {
    const props = Json.child(
      Json.child(parent.attributes(), "nestedRows"),
      preset,
    );
    return props == null ? null : decodeNestedProperties(props);
  }

  /** Read the nested keys (`timeScale`, `heightPx`, `label`) off a preset slot. */
  export function decodeNestedProperties(props: JsonObject): NestedProperties {
    const timeScale = Json.string(props, "timeScale");
    const heightPx = Json.number(props, "heightPx");
    const label = Json.string(props, "label");
    return {
      ...(timeScale != null ? { timeScale } : {}),
      ...(heightPx != null ? { heightPx } : {}),
      ...(label != null ? { label } : {}),
    };
  }
}
